import store from '@/state/store';
import authenticationSlice from '@/state/authentication.slice';
import userSlice from '@/state/user.slice';
import * as api from '@/api/profile';
import { accept, decline } from '@/api/invitation';
import { Invitation, ProfileUpdateRequest } from '@bimdata/definitions/dto';
import { AccessErrorCodes, RegistrationErrorCodes, VerificationErrorCodes } from '@bimdata/definitions/errors';
import { logout } from '../authentication';
import { handleError, reportError } from '../common';
import { ApiError } from '@bimdata/client';
import projectSlice from '@/state/project.slice';
import t from '@/utils/translate';

export const checkPasswordResetClaim = async (id: string): Promise<void> => {
  try {
    const result = await api.checkPasswordResetClaim(id);
    if (!result) {
      store.dispatch(authenticationSlice.actions.finishPasswordReset());
    }
  } catch (error) {
    handleError(error);
  }
};

export const setProfile = async (data: ProfileUpdateRequest) => {
  try {
    const profile = await api.setProfile(data);
    if (profile) {
      store.dispatch(authenticationSlice.actions.updateProfile(profile));
    }
  } catch (error) {
    handleError(error);
  }
};

export const deleteProfile = async () => {
  try {
    await api.deleteProfile();
    await logout();
  } catch (error) {
    handleError(error);
  }
};

export const startEmailVerification = async () => {
  try {
    await api.startEmailVerification();
    store.dispatch(authenticationSlice.actions.startEmailVerification());
  } catch (error) {
    handleError(error);
  }
};

export const confirmEmail = async (code: string) => {
  try {
    await api.confirmEmail(code);
    const p = await api.getProfile();
    store.dispatch(authenticationSlice.actions.updateProfile(p));
    store.dispatch(authenticationSlice.actions.finishEmailVerification());
  } catch (error) {
    if (error instanceof ApiError) {
      let message = error.message;
      if (error.code === 403 || error.code === 400) {
        switch (error.exceptionCode) {
          case VerificationErrorCodes.VERIFICATION_EXPIRED:
            message = t('authentication.message.staleEmailLink');
            break;
          case VerificationErrorCodes.INVALID_VERIFICATION_CODE:
            message = t('authentication.message.invalidEmailLink');
            break;
          case RegistrationErrorCodes.EMAIL_ALREADY_EXISTS:
            message = t('authentication.message.emailBusy');
            break;
          case AccessErrorCodes.ACCESS_DENIED: {
            throw error;
          }
        }
        error.message = message;
        reportError(error);
      }
    } else {
      error = handleError(error);
    }
    throw error;
  }
};

export const startPhoneVerification = async () => {
  try {
    await api.startPhoneVerification();
    store.dispatch(authenticationSlice.actions.startPhoneVerification());
  } catch (error) {
    handleError(error);
  }
};

export const confirmPhone = async (code: string) => {
  try {
    await api.confirmPhone(code);
    store.dispatch(authenticationSlice.actions.finishPhoneVerification());
  } catch (error) {
    handleError(error);
  }
};

export const startPasswordReset = async () => {
  try {
    const claim = await api.startPasswordReset();
    if (claim) {
      store.dispatch(authenticationSlice.actions.startPasswordReset(claim));
    }
  } catch (error) {
    handleError(error);
  }
};

export const setAvatar = async (file: File) => {
  const state = store.getState();
  const userId = state.authentication.profile?.id;
  if (!userId) {
    return;
  }
  try {
    store.dispatch(userSlice.actions.avatarBusy(userId));
    const avatarSource = await api.setAvatar(file);
    store.dispatch(userSlice.actions.avatarIdle({
      userId,
      source: avatarSource
    }));
  } catch (error) {
    store.dispatch(userSlice.actions.avatarError(userId));
    handleError(error);
  }
};

export const deleteAvatar = async () => {
  const state = store.getState();
  const userId = state.authentication.profile?.id;
  if (!userId) {
    return;
  }
  try {
    await api.deleteAvatar();
    store.dispatch(userSlice.actions.avatarIdle({
      userId,
      source: null
    }));
  } catch (error) {
    handleError(error);
  }
};

export const acceptInvitation = async (invitation: Invitation) => {
  try {
    await accept(invitation.project.code);
    store.dispatch(authenticationSlice.actions.removeInvitation(invitation.id));
    store.dispatch(projectSlice.actions.created(invitation.project));
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const declineInvitation = async (invitation: Invitation) => {
  try {
    await decline(invitation.project.code);
    store.dispatch(authenticationSlice.actions.removeInvitation(invitation.id));
  } catch (error) {
    handleError(error);
    throw error;
  }
};
